/** @jsx jsx */
import { type SerializedStyles, css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import type { ImgParams } from 'js/lib/imgix';

import { Typography2, breakpoints } from '@coursera/cds-core';

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);

    ${breakpoints.down('sm')} {
      flex-direction: row;
      gap: var(--cds-spacing-200);
    }
  `,
  titleAndSubtitleContainer: css`
    width: 164px;
    min-height: 80px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    ${breakpoints.down('sm')} {
      width: unset;
      min-height: 58px;
      justify-content: unset;
    }
  `,
  image: css`
    border-radius: 20px;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);

    ${breakpoints.down('sm')} {
      width: 87px;
      height: 58px;
      border-radius: 8px;
    }
  `,
  titleText: css`
    max-height: calc(3 * var(--cds-line-height-250));
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  `,
  subtitleText: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

type StyleKey = keyof typeof styles;

type Props = {
  photoUrl?: string | null;
  title: string;
  subtitle?: React.ReactNode;
  imgParams?: ImgParams;
  cssOverrides?: Partial<Record<StyleKey, SerializedStyles>>;
};

const PathwayContentCard = ({ photoUrl, title, subtitle, imgParams, cssOverrides }: Props) => {
  return (
    <div key={title} css={[cssOverrides?.card || styles.card]}>
      {photoUrl && (
        <Imgix
          src={photoUrl}
          alt={title}
          css={[cssOverrides?.image || styles.image]}
          imgParams={{ fit: 'crop', auto: 'compress', width: 164, height: 109, ...imgParams }}
        />
      )}
      <div css={[cssOverrides?.titleAndSubtitleContainer || styles.titleAndSubtitleContainer]}>
        <Typography2 component="p" variant="actionSecondary" css={[cssOverrides?.titleText || styles.titleText]}>
          {title}
        </Typography2>
        {subtitle && (
          <Typography2 component="p" variant="bodySecondary" css={[cssOverrides?.subtitleText || styles.subtitleText]}>
            {subtitle}
          </Typography2>
        )}
      </div>
    </div>
  );
};

export default PathwayContentCard;
